import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { createArea, patchArea } from '../api/areas';
import { createRack, patchRack } from '../api/racks';
import { loadPolygonData } from '../utils/polygonLoader';
import { normalizePoints } from '../hooks/useCoordinates';
import { useAppActions, useAppState, Rack } from '../app/appContext';

/** Типы продукции для дропдауна. Список — ровно те значения, что реально есть в
 *  проде на 11.09.2026 (60 «Сопутствующая продукция», 38 «Выпечка», 36 «Готовая
 *  еда», 35 «Десерты», 16 «Продукты» у магазина, 6 «Напитки», 4 «Товар на кассе»,
 *  3 «Дополнительный ракурс»), четыре основные — первыми. Менять список здесь,
 *  а не вводить новые значения руками: rack_type в модели это CharField без
 *  choices, и каждый свободный ввод создаёт новую категорию в отчёте. */
export const RACK_TYPE_OPTIONS: readonly string[] = [
  'Готовая еда',
  'Десерты',
  'Выпечка',
  'Сопутствующая продукция',
  'Напитки',
  'Товар на кассе',
  'Продукты',
  'Дополнительный ракурс',
];

export interface RackFormValues {
  name: string;
  rack_type: string;
  perspective_width: string;
  perspective_height: string;
  initial_width: string;
  initial_height: string;
  is_composite: boolean;
  predict_percentage: boolean;
  single_shelf: boolean;
}

interface RackFormProps {
  open: boolean;
  tempPoints: [number, number][];
  onClose: () => void;
  onSuccess?: () => void;
  rackToEdit?: Rack | null;
}

const defaultValues: RackFormValues = {
  name: '',
  rack_type: '',
  perspective_width: '',
  perspective_height: '',
  initial_width: '',
  initial_height: '',
  is_composite: false,
  predict_percentage: true,
  single_shelf: false,
};

const RackForm: React.FC<RackFormProps> = ({ open, tempPoints, onClose, onSuccess, rackToEdit = null }) => {
  const actions = useAppActions();
  const { camera, polygons } = useAppState();
  const selectedCameraId = camera.selectedCameraId;
  const frame = polygons.areas.frame;

  const [form, setForm] = useState<RackFormValues>(defaultValues);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const safeShelves = rackToEdit?.shelves ?? [];
  const shelvesText = safeShelves.length > 0
    ? `${safeShelves.slice(0, 5).join(', ')}${safeShelves.length > 5 ? ` +${safeShelves.length - 5} more` : ''}`
    : '—';

  useEffect(() => {
    if (open) {
      if (rackToEdit) {
        setForm({
          name: rackToEdit.name ?? '',
          rack_type: rackToEdit.rack_type ?? '',
          perspective_width: rackToEdit.perspective_width != null ? String(rackToEdit.perspective_width) : '',
          perspective_height: rackToEdit.perspective_height != null ? String(rackToEdit.perspective_height) : '',
          initial_width: rackToEdit.initial_width != null ? String(rackToEdit.initial_width) : '',
          initial_height: rackToEdit.initial_height != null ? String(rackToEdit.initial_height) : '',
          is_composite: Boolean(rackToEdit.is_composite),
          predict_percentage: Boolean(rackToEdit.predict_percentage),
          single_shelf: Boolean(rackToEdit.single_shelf),
        });
      } else {
        setForm(defaultValues);
      }
      setError('');
      setSaving(false);
    }
  }, [open, rackToEdit]);

  // HTMLSelectElement в объединении — после перевода «Тип продукции» в дропдаун
  // (11.09) тот же обработчик обслуживает и <input>, и <select>. У select нет
  // полей type/checked, поэтому читаем их через опциональное сужение, а не с
  // деструктуризацией, которая на select дала бы undefined для type и ломала
  // ветку чекбоксов.
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const target = e.target;
    const isCheckbox = target instanceof HTMLInputElement && target.type === 'checkbox';
    const next = isCheckbox ? (target as HTMLInputElement).checked : target.value;
    setForm(prev => ({ ...prev, [target.name]: next }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      if (!selectedCameraId || !form.name) {
        setError('Please fill in all required fields.');
        setSaving(false);
        return;
      }
      const basePayload = {
        name: form.name,
        rack_type: form.rack_type || null,
        perspective_width: form.perspective_width ? Number(form.perspective_width) : null,
        perspective_height: form.perspective_height ? Number(form.perspective_height) : null,
        initial_width: form.initial_width ? Number(form.initial_width) : null,
        initial_height: form.initial_height ? Number(form.initial_height) : null,
        is_composite: form.is_composite,
        predict_percentage: form.predict_percentage,
        single_shelf: form.single_shelf,
      };
      if (rackToEdit) {
        await patchRack(rackToEdit.id, basePayload);
        if (rackToEdit.area) {
          await patchArea(rackToEdit.area, { name: form.name });
        }
      } else {
        if (!frame || tempPoints.length < 3) {
          setError('Please complete the polygon before saving.');
          setSaving(false);
          return;
        }
        const normPoints = normalizePoints(tempPoints, { width: frame.width, height: frame.height });
        const areaResp = await createArea({
          direction_vectors: normPoints,
          camera: selectedCameraId,
          name: form.name,
        });
        const areaId = areaResp.data.id;
        await createRack({ ...basePayload, area: areaId });
      }
      if (selectedCameraId) {
        await loadPolygonData(selectedCameraId, actions);
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      setError('Failed to save rack.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{rackToEdit ? 'Edit Rack' : 'Create Rack'}</DialogTitle>
        </DialogHeader>

        {rackToEdit && (
          <div className="mb-3 rounded-md bg-muted px-3 py-2">
            <pre className="font-mono text-xs text-muted-foreground whitespace-pre-wrap">
              {`rack_id: ${rackToEdit.id}\npolygon_id: ${rackToEdit.area}\nshelf_ids: ${shelvesText}`}
            </pre>
          </div>
        )}

        <div className="grid gap-3">
          {(['name', 'rack_type', 'perspective_width', 'perspective_height', 'initial_width', 'initial_height'] as const).map(field => (
            <div key={field} className="grid gap-1.5">
              <Label htmlFor={field}>{field === 'rack_type' ? 'Тип продукции' : field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Label>
              {field === 'rack_type' ? (
                // Дропдаун, а не свободный ввод (Алексей 11.09: «сделать в UI
                // зонирования выбор типа продукции дропдауном»). Свободный текст
                // здесь и породил тип «Батончики» на 10 стеллажах девяти филиалов —
                // отдельную категорию, которая ничем не отличалась от
                // «Сопутствующей продукции», но шла отдельной строкой в отчёте и
                // отдельным весом в статистике (данные переведены в
                // «Сопутствующая продукция» 11.09).
                <Select
                  id={field}
                  name={field}
                  value={String(form[field])}
                  onChange={handleChange}
                >
                  <option value="">— не задан —</option>
                  {RACK_TYPE_OPTIONS.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                  {/* Значение из базы, которого нет в списке, не теряем молча:
                      показываем как есть, иначе редактирование такого стеллажа
                      незаметно переписало бы ему тип. */}
                  {form.rack_type && !RACK_TYPE_OPTIONS.includes(form.rack_type) && (
                    <option value={form.rack_type}>{form.rack_type}</option>
                  )}
                </Select>
              ) : (
                <Input
                  id={field}
                  name={field}
                  value={String(form[field])}
                  onChange={handleChange}
                  type={field.includes('width') || field.includes('height') ? 'number' : 'text'}
                  required={field === 'name'}
                />
              )}
            </div>
          ))}

          {(['is_composite', 'predict_percentage', 'single_shelf'] as const).map(field => (
            <label key={field} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                name={field}
                checked={Boolean(form[field])}
                onChange={handleChange}
                className="rounded"
              />
              {field === 'is_composite' ? 'Is Composite' : field === 'single_shelf' ? 'Single shelf = rack' : 'Predict Percentage'}
            </label>
          ))}
        </div>

        {error && (
          <div className="mt-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RackForm;
